import { useState, useRef } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';
import {
  Package,
  Calendar,
  MapPin,
  Layers,
  MoreVertical,
  Edit,
  Trash2,
  QrCode,
  Printer,
  CheckCircle,
  Clock,
  AlertTriangle,
  Archive,
  Play,
  Square,
  Snowflake,
} from 'lucide-react';
import { CuttingTimer } from '@/components/Cutting';
import { FreezerOutTimeTimer } from '@/components/Freezer';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { PlotterCut, PlotterCutStatus } from '@/types/legacy';
import { useReactToPrint } from 'react-to-print';

const statusConfig: Record<PlotterCutStatus, { label: string; color: string; icon: React.ReactNode }> = {
  available: { label: 'Disponible', color: 'bg-green-500/10 text-green-600 border-green-500/30', icon: <CheckCircle className="w-3 h-3" /> },
  reserved: { label: 'Reservado', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30', icon: <Clock className="w-3 h-3" /> },
  in_use: { label: 'En Uso', color: 'bg-blue-500/10 text-blue-600 border-blue-500/30', icon: <Package className="w-3 h-3" /> },
  consumed: { label: 'Consumido', color: 'bg-gray-500/10 text-gray-600 border-gray-500/30', icon: <Archive className="w-3 h-3" /> },
  expired: { label: 'Caducado', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: <AlertTriangle className="w-3 h-3" /> },
  damaged: { label: 'Dañado', color: 'bg-red-500/10 text-red-600 border-red-500/30', icon: <AlertTriangle className="w-3 h-3" /> },
};

const materialTypeLabels: Record<string, string> = {
  carbon_fiber: 'Fibra de Carbono',
  fiberglass: 'Fibra de Vidrio',
  kevlar: 'Kevlar',
  hybrid: 'Híbrido',
  prepreg: 'Prepreg',
  dry_fiber: 'Fibra Seca',
  other: 'Otro',
};

interface PlotterCutCardProps {
  cut: PlotterCut;
  onEdit: (cut: PlotterCut) => void;
  onDelete: (cut: PlotterCut) => void;
  onStatusChange: (cut: PlotterCut, status: PlotterCutStatus) => void;
  onRefresh?: () => void;
}

export function PlotterCutCard({ cut, onEdit, onDelete, onStatusChange, onRefresh }: PlotterCutCardProps) {
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Funciones para cronómetro de corte
  const handleStartCutting = async () => {
    const { error } = await api
      .from('plotter_cuts')
      .update({
        cutting_started_at: new Date().toISOString(),
        cutting_operator_id: user?.id,
        status: 'in_use',
      })
      .eq('id', cut.id);
    
    if (error) {
      toast.error('Error al iniciar corte');
      return;
    }
    toast.success('Corte iniciado');
    onRefresh?.();
  };

  const handleStopCutting = async () => {
    const startedAt = cut.cutting_started_at ? new Date(cut.cutting_started_at) : new Date();
    const durationSeconds = Math.round((Date.now() - startedAt.getTime()) / 1000);
    
    const { error } = await api
      .from('plotter_cuts')
      .update({
        cutting_finished_at: new Date().toISOString(),
        cutting_duration_seconds: durationSeconds,
      })
      .eq('id', cut.id);
    
    if (error) {
      toast.error('Error al finalizar corte');
      return;
    }
    toast.success(`Corte finalizado en ${Math.floor(durationSeconds / 60)}m ${durationSeconds % 60}s`);
    onRefresh?.();
  };

  // Función para guardar en congelador
  const handleReturnToFreezer = async () => {
    const { error } = await api
      .from('plotter_cuts')
      .update({
        removed_from_freezer_at: null,
        removed_from_freezer_by: null,
        status: 'available',
      })
      .eq('id', cut.id);
    
    if (error) {
      toast.error('Error al guardar en congelador');
      return;
    }
    toast.success('Material guardado en congelador');
    onRefresh?.();
  };

  // Funcion para sacar del congelador
  const handleRemoveFromFreezer = async () => {
    const { error } = await api
      .from('plotter_cuts')
      .update({
        removed_from_freezer_at: new Date().toISOString(),
        removed_from_freezer_by: user?.id,
        thaw_count: (cut.thaw_count || 0) + 1,
      })
      .eq('id', cut.id);
    
    if (error) {
      toast.error('Error al sacar del congelador');
      return;
    }
    toast.success('Material sacado del congelador - Cronometro iniciado');
    onRefresh?.();
  };

  const isOutOfFreezer = !!cut.removed_from_freezer_at;
  const isCutting = !!cut.cutting_started_at && !cut.cutting_finished_at;
  const canStartCutting = cut.status === 'in_use' && !cut.cutting_started_at;

  const status = statusConfig[cut.status];

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `QR-${cut.package_code}`,
  });

  const qrData = cut.qr_code || JSON.stringify({
    code: cut.package_code,
    name: cut.package_name,
    type: cut.material_type,
    layers: cut.total_layers,
    batch: cut.batch_number,
    cut_date: cut.cut_date,
  });

  return (
    <>
      <Card className="p-4 hover:shadow-md transition-all">
        <div className="flex items-start gap-4">
          {/* QR Code mini */}
          <div
            className="flex-shrink-0 p-2 bg-white rounded-lg cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => setQrDialogOpen(true)}
          >
            <QRCodeSVG value={qrData} size={64} level="M" />
          </div>

          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-primary">{cut.package_code}</span>
                  <Badge className={status.color} variant="outline">
                    {status.icon}
                    <span className="ml-1">{status.label}</span>
                  </Badge>
                </div>
                <h4 className="font-semibold mt-1">{cut.package_name}</h4>
              </div>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setQrDialogOpen(true)}>
                    <QrCode className="w-4 h-4 mr-2" />
                    Ver QR
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onEdit(cut)}>
                    <Edit className="w-4 h-4 mr-2" />
                    Editar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {cut.status === 'available' && (
                    <DropdownMenuItem onClick={() => onStatusChange(cut, 'reserved')}>
                      <Clock className="w-4 h-4 mr-2" />
                      Reservar
                    </DropdownMenuItem>
                  )}
                  {cut.status === 'reserved' && (
                    <>
                      <DropdownMenuItem onClick={() => onStatusChange(cut, 'in_use')}>
                        <Package className="w-4 h-4 mr-2" />
                        Marcar En Uso
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => onStatusChange(cut, 'available')}>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Liberar
                      </DropdownMenuItem>
                    </>
                  )}
                  {cut.status === 'in_use' && (
                    <DropdownMenuItem onClick={() => onStatusChange(cut, 'consumed')}>
                      <Archive className="w-4 h-4 mr-2" />
                      Marcar Consumido
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  {/* Opciones de congelador */}
                  {!isOutOfFreezer && cut.status === 'available' && (
                    <DropdownMenuItem onClick={handleRemoveFromFreezer} className="text-blue-600">
                      <Snowflake className="w-4 h-4 mr-2" />
                      Sacar del Congelador
                    </DropdownMenuItem>
                  )}
                  {isOutOfFreezer && (
                    <DropdownMenuItem onClick={handleReturnToFreezer} className="text-blue-600">
                      <Snowflake className="w-4 h-4 mr-2" />
                      Guardar en Congelador
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => onDelete(cut)}
                    className="text-destructive"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Info del material */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Layers className="w-4 h-4" />
                {cut.total_layers} capas
              </span>
              {cut.material_type && (
                <Badge variant="secondary" className="font-normal">
                  {materialTypeLabels[cut.material_type] || cut.material_type}
                </Badge>
              )}
              {cut.dimensions && (
                <span>{cut.dimensions}</span>
              )}
              {cut.weight_grams && (
                <span>{cut.weight_grams}g</span>
              )}
            </div>

            {/* Materiales detallados */}
            {cut.materials && cut.materials.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {cut.materials.map((m, i) => (
                  <Badge key={i} variant="outline" className="text-xs">
                    {m.name} ({m.layers}x)
                  </Badge>
                ))}
              </div>
            )}

            {/* Ubicación y fecha */}
            <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
              {cut.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {cut.location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Cortado: {format(new Date(cut.cut_date), 'dd/MM/yyyy', { locale: es })}
              </span>
              {cut.batch_number && (
                <span>Lote: {cut.batch_number}</span>
              )}
            </div>

            {/* Cronómetros */}
            {(isOutOfFreezer || isCutting || canStartCutting) && (
              <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t">
                {/* Cronómetro de congelador */}
                {isOutOfFreezer && (
                  <FreezerOutTimeTimer
                    removedAt={cut.removed_from_freezer_at!}
                    maxMinutes={cut.max_out_time_minutes || 120}
                    materialName={cut.package_name}
                    batchNumber={cut.batch_number}
                    onReturn={handleReturnToFreezer}
                    compact
                  />
                )}
                
                {/* Cronómetro de corte */}
                {(isCutting || canStartCutting) && (
                  <CuttingTimer
                    startedAt={cut.cutting_started_at}
                    onStart={canStartCutting ? handleStartCutting : undefined}
                    onStop={isCutting ? handleStopCutting : undefined}
                    compact
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Dialog del QR para imprimir */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5" />
              QR del Paquete
            </DialogTitle>
          </DialogHeader>

          {/* Contenido imprimible */}
          <div ref={printRef} className="p-6 bg-white">
            <div className="text-center space-y-4">
              <QRCodeSVG
                value={qrData}
                size={200}
                level="H"
                includeMargin
                className="mx-auto"
              />
              <div className="space-y-1">
                <p className="font-mono text-xl font-bold">{cut.package_code}</p>
                <p className="font-medium">{cut.package_name}</p>
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <span>{cut.total_layers} capas</span>
                  {cut.material_type && (
                    <>
                      <span>•</span>
                      <span>{materialTypeLabels[cut.material_type]}</span>
                    </>
                  )}
                </div>
                {cut.batch_number && (
                  <p className="text-xs text-muted-foreground">Lote: {cut.batch_number}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Corte: {format(new Date(cut.cut_date), 'dd/MM/yyyy')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-4">
            <Button variant="outline" onClick={() => setQrDialogOpen(false)}>
              Cerrar
            </Button>
            <Button onClick={() => handlePrint()} className="bg-gradient-magma">
              <Printer className="w-4 h-4 mr-2" />
              Imprimir QR
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
